import React, { useState, useId, useCallback } from 'react';
import styled from 'styled-components';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { breakpoints } from 'design-system/src/tokens/breakpoints';
import Button from 'design-system/src/components/Button/Button';
import { Checkbox } from 'design-system/src/components/Checkbox/Checkbox';
import { InfoBox } from 'design-system/src/components/InfoBox/InfoBox';
import { PrivacyPolicy } from '../PrivacyPolicy/PrivacyPolicy';
import { Input } from '../Input';
import { Controller, useForm } from 'react-hook-form';
import { ControllerWrapper } from './ControllerWrapper';
import {
  PageContainer,
  StyledSignupForm,
  StyledCheckboxRow,
  StyledCheckboxItem,
  DesktopContainer,
  MobileContainer,
  NameRow,
} from './styles';
import { useSignupDocVariantsFromUrl } from '../legacySignup/useSignupDocVariantsFromUrl';
import { Role } from '../../types';
import { AccountSetupFormState } from './types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { client } from '../../api';
import toast from 'react-hot-toast';
import { Toast } from 'design-system/src/components/Toast/Toast';
import { ProgressCard } from 'design-system/src/components/ProgressCard/ProgressCard';
import { Dropdown, DropdownContainer } from '../Dropdown.tsx';
import { FormLabel } from '../FormLabel';
import { getRoleOptions, getRequiredErrorMessage } from './utils.tsx';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { SignupErrorMessage } from './SignupErrorMessage';
import { handleServerError } from '../../utils';
import { createTrackingProps } from 'shared-utils/analytics';

const DESKTOP_INNER_BOTTOM_SPACE = 32;
const MOBILE_INNER_BOTTOM_SPACE = 18;
const MOBILE_INNER_HEIGHT = 600;

const StyledScrollableContent = styled.div`
  overflow-y: auto;
  padding: 24px;
  padding-bottom: 24px;
  height: 90dvh;
  min-height: 400px;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  @media (max-width: ${breakpoints.mobileMax}px) {
    max-height: ${MOBILE_INNER_HEIGHT}px;
  }
`;

const StyledFooter = styled.div`
  background: var(--white);
  width: 100%;
  margin-top: 32px;
`;

const StyledUserInfoField = styled.div`
  flex: 1;
`;

const StyledFooterButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 16px;

  @media (max-width: ${breakpoints.mobileMax}px) {
    margin-top: 4px;
  }
`;

const StyledInfoBoxWrapper = styled.div`
  margin: 24px 0px;

  @media (max-width: ${breakpoints.mobileMax}px) {
    margin: 16px 0px;
  }
`;

const StyledTerms = styled(PrivacyPolicy)`
  padding-bottom: 0px;
`;

const StyledScrollToSignButton = styled.div<{ $isHidden: boolean }>`
  position: absolute;
  bottom: ${DESKTOP_INNER_BOTTOM_SPACE}px;
  background: var(--white);
  padding: 0px 32px;
  display: ${({ $isHidden }) => ($isHidden ? 'none' : 'flex')};
  justify-content: center;
  left: 0;
  right: 0;

  @media (max-width: ${breakpoints.mobileMax}px) {
    bottom: ${MOBILE_INNER_BOTTOM_SPACE}px;
    padding: 0px 16px;
  }
`;

const StyledFadeOverlay = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  height: 60px;
  background: linear-gradient(to bottom, transparent, var(--white));
`;

const StyledHeaderContainer = styled.div`
  align-self: flex-start;
`;

const StyledCheckboxLabel = styled.label`
  width: 100%;
`;

type FooterFormData = {
  firstName: string;
  lastName: string;
  role: Role;
  legalPracticeName: string;
};

type TermsAgreementFormProps = {
  onNext: () => void;
  firstName?: string;
  lastName?: string;
  role?: Role;
  legalPracticeName?: string;
  defaultValues?: {
    tos_accepted?: boolean;
    baa_accepted?: boolean;
    aa_accepted?: boolean;
  };
  onFormDataUpdate?: (updates: Partial<AccountSetupFormState>) => void;
};

export type TermsOfServiceProps = {
  className?: string;
} & TermsAgreementFormProps;

type UpdateProfileInformationData = {
  first_name: string;
  last_name: string;
  role: Role;
  practice_name: string;
  accepted_terms_at: string;
  tos_s3_key: string;
  tos_version_accepted: string;
  baa_s3_key: string;
  baa_version_accepted: string;
  aa_s3_key: string;
  aa_version_accepted: string;
};

const TermsAgreementForm = ({
  onNext,
  firstName = '',
  lastName = '',
  role = undefined,
  legalPracticeName = '',
  defaultValues,
  onFormDataUpdate,
}: TermsAgreementFormProps) => {
  const [hasAcceptedTermsOfService, setHasAcceptedTermsOfService] = useState(defaultValues?.tos_accepted || false);
  const [hasAcceptedBusinessAssociate, setHasAcceptedBusinessAssociate] = useState(
    defaultValues?.baa_accepted || false,
  );
  const { tosVariant, baaVariant, aaVariant } = useSignupDocVariantsFromUrl();

  const tosCheckboxId = useId();
  const baaCheckboxId = useId();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FooterFormData>({
    defaultValues: {
      firstName,
      lastName,
      role,
      legalPracticeName,
    },
  });

  const query = useQuery({
    queryKey: ['documentVersions'],
    queryFn: () => {
      return client
        .get(`/signup/document-versions?tos_variant=${tosVariant}&baa_variant=${baaVariant}&aa_variant=${aaVariant}`)
        .then((res) => {
          return res.data;
        })
        .catch((error) => {
          const serverErrorMessage = handleServerError(error);
          toast.custom(() => <Toast variant="error">{serverErrorMessage}</Toast>);
        });
    },
  });

  const [error, setError] = useState<string | null>(null);

  const { mutate: submitTermsOfService, isPending: isSubmittingTermsOfService } = useMutation({
    mutationFn: async (data: UpdateProfileInformationData) => {
      const response = await client.post(`/signup/submit-terms-of-service`, data);
      return response.data;
    },
    onSuccess: () => {
      setError(null);
      onNext();
    },
    onError: (error: any) => {
      console.error('Error updating profile information:', error);
      const serverErrorMessage = handleServerError(error);
      setError(serverErrorMessage);
    },
  });

  const handleTermsOfServiceChange = () => {
    const newValue = !hasAcceptedTermsOfService;
    setHasAcceptedTermsOfService(newValue);
    onFormDataUpdate?.({
      terms_accepted: { tos: newValue },
    });
  };

  const handleBusinessAssociateChange = () => {
    const newValue = !hasAcceptedBusinessAssociate;
    setHasAcceptedBusinessAssociate(newValue);
    onFormDataUpdate?.({
      terms_accepted: { baa: newValue },
    });
  };

  const handleAgreeAndSignUp = (formData: FooterFormData) => {
    if (!query.data) {
      const serverErrorMessage = 'Unable to load document versions. Please try again.';
      toast.custom(() => <Toast variant="error">{serverErrorMessage}</Toast>);
      return;
    }

    const updateData: Partial<AccountSetupFormState> = {
      accepted_terms_at: new Date().toISOString(),
      terms_s3_keys: {
        tos: tosVariant,
        baa: baaVariant,
        aa: aaVariant,
      },
      terms_versions: {
        tos: query.data.tos_version,
        baa: query.data.baa_version,
        aa: query.data.aa_version,
      },
      terms_accepted: {
        tos: hasAcceptedTermsOfService,
        baa: hasAcceptedBusinessAssociate,
        aa: defaultValues?.aa_accepted || false,
      },
    };

    onFormDataUpdate?.(updateData);

    submitTermsOfService({
      first_name: formData.firstName,
      last_name: formData.lastName,
      role: formData.role,
      practice_name: formData.legalPracticeName,
      accepted_terms_at: new Date().toISOString(),
      tos_s3_key: tosVariant,
      tos_version_accepted: query.data.tos_version,
      baa_s3_key: baaVariant,
      baa_version_accepted: query.data.baa_version,
      aa_s3_key: aaVariant,
      aa_version_accepted: query.data.aa_version,
    });
  };

  const canAgreeAndSignUp = hasAcceptedTermsOfService && hasAcceptedBusinessAssociate;

  const urlSearchString = window.location.search;
  const standaloneTermsUrl = `/tos-full${urlSearchString || ''}`;

  return (
    <StyledFooter>
      <StyledInfoBoxWrapper>
        <InfoBox variant="light" noBottomMargin noTopMargin>
          <Typography>Please confirm that the information below is accurate.</Typography>
        </InfoBox>
      </StyledInfoBoxWrapper>
      <StyledSignupForm onSubmit={handleSubmit(handleAgreeAndSignUp)}>
        <NameRow>
          <StyledUserInfoField>
            <ControllerWrapper>
              <Controller
                control={control}
                name="firstName"
                rules={{ required: { value: true, message: getRequiredErrorMessage('First name') } }}
                render={({ field }) => (
                  <Input
                    label="First Name"
                    hasError={!!errors.firstName}
                    placeholder="John"
                    {...field}
                    {...createTrackingProps({ unmask: true })}
                  />
                )}
              />
              {errors.firstName && <ErrorMessage>{errors.firstName.message}</ErrorMessage>}
            </ControllerWrapper>
          </StyledUserInfoField>
          <StyledUserInfoField>
            <ControllerWrapper>
              <Controller
                control={control}
                name="lastName"
                rules={{ required: { value: true, message: getRequiredErrorMessage('Last name') } }}
                render={({ field }) => (
                  <Input
                    label="Last Name"
                    hasError={!!errors.lastName}
                    placeholder="Doe"
                    {...field}
                    {...createTrackingProps({ unmask: true })}
                  />
                )}
              />
              {errors.lastName && <ErrorMessage>{errors.lastName.message}</ErrorMessage>}
            </ControllerWrapper>
          </StyledUserInfoField>
        </NameRow>

        <ControllerWrapper>
          <Controller
            control={control}
            name="role"
            rules={{ required: { value: true, message: getRequiredErrorMessage('Role') } }}
            render={({ field }) => (
              <DropdownContainer>
                <FormLabel id="role-label">Your Role</FormLabel>
                <Dropdown
                  labelId="role-label"
                  id="role"
                  label="Your Role"
                  {...field}
                  {...createTrackingProps({ unmask: true })}
                >
                  {getRoleOptions()}
                </Dropdown>
                {errors.role && <ErrorMessage>{errors.role.message}</ErrorMessage>}
              </DropdownContainer>
            )}
          />
        </ControllerWrapper>

        <DesktopContainer>
          <ControllerWrapper>
            <Controller
              control={control}
              name="legalPracticeName"
              rules={{ required: { value: true, message: getRequiredErrorMessage('Legal practice name') } }}
              render={({ field }) => (
                <Input
                  label="Legal Practice Name"
                  helperText="Provide the full legal name of your practice as used on official documents. Avoid abbreviations or informal variations."
                  hasError={!!errors.legalPracticeName}
                  placeholder="Tribeca Doctors LLC"
                  {...field}
                  {...createTrackingProps({ unmask: true })}
                />
              )}
            />
            {errors.legalPracticeName && <ErrorMessage>{errors.legalPracticeName.message}</ErrorMessage>}
          </ControllerWrapper>
        </DesktopContainer>
        <MobileContainer>
          <ControllerWrapper>
            <Controller
              control={control}
              name="legalPracticeName"
              rules={{ required: { value: true, message: getRequiredErrorMessage('Legal practice name') } }}
              render={({ field }) => (
                <Input
                  label="Legal Practice Name"
                  hasError={!!errors.legalPracticeName}
                  placeholder="Tribeca Doctors LLC"
                  {...field}
                  {...createTrackingProps({ unmask: true })}
                />
              )}
            />
            {errors.legalPracticeName && <ErrorMessage>{errors.legalPracticeName.message}</ErrorMessage>}
          </ControllerWrapper>
        </MobileContainer>

        <StyledCheckboxRow>
          <StyledCheckboxItem>
            <Checkbox checked={hasAcceptedTermsOfService} onChange={handleTermsOfServiceChange} id={tosCheckboxId} />
            <StyledCheckboxLabel htmlFor={tosCheckboxId}>
              <Typography styledAs="bodyExtraSmallDMSans" renderedAs="div">
                I have read and agree to the{' '}
                <Button
                  variant="link"
                  href={standaloneTermsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  textStyledAs="bodyExtraSmallDMSans"
                >
                  <strong>Tandem Terms of Service / Privacy Notice</strong>
                  <span className="sr-only">Opens in a new tab</span>
                </Button>
              </Typography>
            </StyledCheckboxLabel>
          </StyledCheckboxItem>
          <StyledCheckboxItem>
            <Checkbox
              checked={hasAcceptedBusinessAssociate}
              onChange={handleBusinessAssociateChange}
              id={baaCheckboxId}
            />
            <StyledCheckboxLabel htmlFor={baaCheckboxId}>
              <Typography styledAs="bodyExtraSmallDMSans">
                I have read and agree to the{' '}
                <Button
                  variant="link"
                  href={standaloneTermsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  textStyledAs="bodyExtraSmallDMSans"
                >
                  <strong>Tandem Business Associate Agreement</strong>
                  <span className="sr-only">Opens in a new tab</span>
                </Button>
              </Typography>
            </StyledCheckboxLabel>
          </StyledCheckboxItem>
        </StyledCheckboxRow>

        {error && <SignupErrorMessage error={error} marginBottom="16px" />}

        <StyledFooterButtonContainer>
          <Button type="submit" disabled={!canAgreeAndSignUp} width="full" isLoading={isSubmittingTermsOfService}>
            Agree and sign up
          </Button>
        </StyledFooterButtonContainer>
      </StyledSignupForm>
    </StyledFooter>
  );
};

export const TermsOfService = ({
  onNext,
  firstName = '',
  lastName = '',
  role = undefined,
  legalPracticeName = '',
  defaultValues,
  onFormDataUpdate,
}: TermsOfServiceProps) => {
  const [isAtBottom, setIsAtBottom] = useState(false);
  const { tosVariant, baaVariant, aaVariant } = useSignupDocVariantsFromUrl();

  const { data: signupData, isLoading: isLoadingSignupData } = useQuery({
    queryKey: ['signup-data'],
    queryFn: async () => {
      const response = await client.get(`/signup/get-by-stytch-user-id`);
      return response.data;
    },
  });

  const checkIfAtBottom = useCallback((element: HTMLDivElement) => {
    const { scrollTop, scrollHeight, clientHeight } = element;
    const threshold = 50;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - threshold;
    setIsAtBottom(isAtBottom);
  }, []);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      checkIfAtBottom(event.currentTarget);
    },
    [checkIfAtBottom],
  );

  const handleScrollToSign = () => {
    const scrollableContent = document.querySelector('[data-scrollable-content]') as HTMLDivElement;
    if (scrollableContent) {
      scrollableContent.scrollTo({
        top: scrollableContent.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  if (isLoadingSignupData) {
    return (
      <PageContainer>
        <LoadingSpinner />
      </PageContainer>
    );
  }

  const finalFirstName = signupData?.first_name || firstName;
  const finalLastName = signupData?.last_name || lastName;
  const finalRole = signupData?.role || role;
  const finalLegalPracticeName = signupData?.practice_name || legalPracticeName;

  return (
    <ProgressCard progressBar={{ current: 2, total: 3 }} desktopWidth="920px" noGap>
      <StyledScrollableContent onScroll={handleScroll} data-scrollable-content>
        <StyledHeaderContainer>
          <Typography styledAs="h6" marginBottom="8px">
            Before we continue, please read
          </Typography>
        </StyledHeaderContainer>
        <StyledTerms fileNames={[tosVariant, baaVariant, aaVariant]} />
        <TermsAgreementForm
          onNext={onNext}
          firstName={finalFirstName}
          lastName={finalLastName}
          role={finalRole}
          legalPracticeName={finalLegalPracticeName}
          defaultValues={defaultValues}
          onFormDataUpdate={onFormDataUpdate}
        />
      </StyledScrollableContent>

      <StyledScrollToSignButton $isHidden={isAtBottom}>
        <StyledFadeOverlay />
        <Button variant="formScroll" size="lg" width="full" onClick={handleScrollToSign}>
          Scroll to sign
        </Button>
      </StyledScrollToSignButton>
    </ProgressCard>
  );
};

export default TermsOfService;
